import { AxiosError } from 'axios';
import { useIsMutating, useMutation, UseMutationResult } from 'react-query';
import { api } from '../api';
import { WorkpaperProcedure } from '../types/workpaper-procedure';
import queryClient from '../query-client';
import { GetWorkpapersProceduresResponse } from './use-workpaper-procedures';
import { GetProcedureResponse } from './use-workpaper-procedure';

export type UpdateWorkpaperProceduresRequest = {
    id: string;
    fields: Partial<WorkpaperProcedure>;
}[];

async function updateWorkpaperProcedures(request: UpdateWorkpaperProceduresRequest, workpaperId: string): Promise<void> {
    const { data } = await api.patch(`/workpapers/${workpaperId}/procedures`, request);
    return data;
}

export function useUpdateWorkpaperProcedures(
    workpaperId: string
): UseMutationResult<void, AxiosError, UpdateWorkpaperProceduresRequest, unknown> {
    return useMutation((req) => updateWorkpaperProcedures(req, workpaperId), {
        mutationKey: 'wp-procedure-update',
        onMutate: (fields) => {
            queryClient.setQueriesData<GetWorkpapersProceduresResponse | GetProcedureResponse>(
                ['workpapers', workpaperId, 'procedures'],
                (prev) => {
                    if (!prev) {
                        return { workpaperProcedures: [] };
                    }

                    if ('workpaperProcedure' in prev) {
                        return prev;
                    }

                    return {
                        workpaperProcedures: prev.workpaperProcedures.map((wp) => {
                            const field = fields.find((f) => f.id === wp.procedure.id);
                            if (field) {
                                return { ...wp, procedure: { ...wp.procedure, ...field.fields } };
                            }
                            return wp;
                        }),
                    };
                }
            );
        },

        onSettled: () => {
            queryClient.invalidateQueries(['workpapers', workpaperId, 'procedures']);
            queryClient.invalidateQueries(['workpapers', workpaperId, 'procedures-overview']);
            queryClient.invalidateQueries(['workpapers', workpaperId, 'procedures-notes']);
            queryClient.invalidateQueries(['workpapers', workpaperId, 'procedure-categories']);
        },
    });
}
